import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import {
  AnnotatedCommentInput,
  CommentsPanel,
  parseAnnotation,
  useComments,
  useCommentsScope,
  useCreateFileComment,
  useDeselectOnOutsideClick,
  useFlashComment,
  usePendingCommentScroll,
  useReplayAnnotation,
  useRootSortOrder,
  useSelectionActions,
  useShowResolved,
  type AnnotatedCommentMediaKind,
} from '@/features/comment-on-file';
import { usePeopleMentionOptions } from '@/entities/project-member';
import { useAssetApprovalChain } from '@/entities/asset-approval-chain';
import { ApprovalTimeline } from '@/features/approve-asset-step';
import { AssetApprovalChainControl } from '@/features/assign-asset-approval-chain';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { DetailSidebar, ScopedPermission, type DetailSidebarView } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { formatFileSize, getExtensionFromFileType } from '@/shared/utils/format';
import { FILE_STATUS_CONFIG } from '../../../Folders/folders.constants';
import type { AssetFile } from '../../assetDetail.types';
import { CommentThread } from '../CommentThread';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  file: AssetFile;
  projectId: string;
  assetId: string;
  folderId?: string;
  /**
   * Whether the approval panel is worth a rail at all: the flag is on AND either
   * a chain is assigned or the reader may assign one. The page owns that
   * decision so the toggle and this rail cannot disagree.
   */
  showApprovalPanel: boolean;
  /** `project:edit-details` — gates assigning and changing the asset's chain. */
  canManageApprovalChain: boolean;
  /** Undefined collapses the rail; `DetailSidebar` animates that itself. */
  view: 'approval' | 'details' | 'comments' | undefined;
  width: number;
  onResize: (next: number) => void;
  onResizeEnd: (next: number) => void;
  /**
   * Opens the chain editor on the chain this asset runs. Omitted when the host
   * has nowhere to put the editor panel.
   */
  onEditApprovalChain?: (approvalChainId: string) => void;
}

// Picks the right Annotation discriminant for the composer. Documents that
// aren't PDFs fall back to image so legacy save semantics still apply.
function toComposerKind(file: AssetFile): AnnotatedCommentMediaKind {
  if (file.assetType === 'video') return 'video';
  if (file.assetType === 'document' && file.fileType === 'application/pdf') return 'pdf';
  return 'image';
}

/**
 * The asset detail right rail: Comments, Details and (when the asset runs an
 * approval chain) Approval, in one collapsible sidebar.
 *
 * Every comments hook stays HERE rather than inside the Comments tab body,
 * because Radix unmounts inactive tab content: moving them down would stop the
 * deep-link and sort-sync effects, and stale the tab's comment count, whenever
 * the reader switched to another tab.
 */
export function AssetSidebar(props: Props) {
  const { t } = useTranslation('projects');
  const { file, projectId, assetId, folderId, view } = props;

  const { comments, totalAll, isLoading, hasMore, isFetchingMore, loadMore } = useComments();
  // People mentions: project members, resolved from the seeded comments scope.
  const scope = useCommentsScope();
  const peopleOptions = usePeopleMentionOptions(scope.workspaceId, scope.platformProjectId);
  const { createComment } = useCreateFileComment();
  const assetKind = toComposerKind(file);
  const [searchParams, setSearchParams] = useSearchParams();
  const replayAnnotation = useReplayAnnotation();
  const flashComment = useFlashComment();
  const { selectComment } = useSelectionActions();
  useDeselectOnOutsideClick();
  const { rootSortOrder, setRootSortOrder } = useRootSortOrder();
  const { showResolved, setShowResolved } = useShowResolved();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const processedCidRef = useRef<string | null>(null);
  const { setPendingScrollId, registerRow } = usePendingCommentScroll();

  const { data: approvalChain, isLoading: isLoadingApprovalChain } = useAssetApprovalChain(
    projectId,
    assetId,
  );

  const rootComments = comments.filter((c) => !c.parentId);
  const isEmpty = rootComments.length === 0;
  // Comments exist but none are visible ⇒ they're all resolved and hidden.
  const hasHiddenResolved = isEmpty && totalAll > 0;

  const cid = searchParams.get('cid');
  const sortParam = searchParams.get('sort');

  useEffect(() => {
    // eslint-disable-next-line i18next/no-literal-string -- sort direction values carried in the URL
    const nextOrder = sortParam === 'desc' ? 'desc' : 'asc';
    setRootSortOrder(nextOrder);
  }, [sortParam, setRootSortOrder]);

  const handleSortChange = (nextOrder: 'asc' | 'desc') => {
    setRootSortOrder(nextOrder);
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (nextOrder === 'desc') {
          nextParams.set('sort', 'desc');
        } else {
          // eslint-disable-next-line i18next/no-literal-string -- URL query parameter name
          nextParams.delete('sort');
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (!cid || isLoading) return;
    if (processedCidRef.current === cid) return;

    const target = rootComments.find((c) => c.id === cid && !c.deleted);
    if (!target) return;

    processedCidRef.current = cid;

    // Handed to the pending-scroll mechanism rather than read off the DOM: the
    // rail can be collapsed when a shared link is followed, so the row does not
    // exist yet. This lands whenever the list mounts.
    setPendingScrollId(target.id);

    replayAnnotation(target.id, parseAnnotation(target.annotation), target.versionId ?? '');
    selectComment(target.id);
    flashComment(target.id);
  }, [
    cid,
    isLoading,
    rootComments,
    setPendingScrollId,
    replayAnnotation,
    selectComment,
    flashComment,
  ]);

  const commentsView: DetailSidebarView = {
    // eslint-disable-next-line i18next/no-literal-string -- sidebar view value
    value: 'comments',
    label: t('projectDetail.assetSidebar.comments'),
    content: (
      <CommentsPanel
        isLoading={isLoading}
        showResolved={showResolved}
        onToggleResolved={() => setShowResolved(!showResolved)}
        hasComments={totalAll > 0}
        sortOrder={rootSortOrder}
        onSortChange={handleSortChange}
        sortable={rootComments.length > 1}
        isEmpty={isEmpty}
        emptyTitle={
          hasHiddenResolved
            ? t('projectDetail.assetSidebar.noPendingComments')
            : t('projectDetail.assetSidebar.noCommentLeftYet')
        }
        emptyDescription={
          hasHiddenResolved ? (
            <Trans
              i18nKey="projectDetail.assetSidebar.toggleResolvedCommentsToSee"
              ns="projects"
              components={{ br: <br /> }}
            />
          ) : (
            t('projectDetail.assetSidebar.commentsWillAppearHereOnce')
          )
        }
        hasMore={hasMore}
        isFetchingMore={isFetchingMore}
        onLoadMore={loadMore}
        scrollRef={scrollContainerRef}
        footer={
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectComment]}>
            <AnnotatedCommentInput
              assetKind={assetKind}
              mentionOptions={peopleOptions}
              onSave={async (content, annotation, attachmentAssetIds) => {
                const created = await createComment(content, {
                  ...(annotation ? { annotation } : {}),
                  attachmentAssetIds,
                });
                if (created?.id) setPendingScrollId(created.id);
              }}
              placeholder={t('projectDetail.assetSidebar.addAComment')}
            />
          </ScopedPermission>
        }
      >
        {rootComments.map((comment) => (
          <div key={comment.id} ref={registerRow(comment.id)} data-comment-id={comment.id}>
            <CommentThread comment={comment} assetKind={assetKind} mentionOptions={peopleOptions} />
          </div>
        ))}
      </CommentsPanel>
    ),
  };

  const detailsView: DetailSidebarView = {
    // eslint-disable-next-line i18next/no-literal-string -- sidebar view value
    value: 'details',
    label: t('projectDetail.assetSidebar.details'),
    content: (
      <div className="flex flex-col gap-sm p-md">
        <DetailRow
          label={t('projectDetail.assetSidebar.fileName')}
          value={file.fileName}
          truncate
        />
        <DetailRow
          label={t('projectDetail.assetSidebar.type')}
          value={getExtensionFromFileType(file.fileType, file.fileName)}
        />
        <DetailRow
          label={t('projectDetail.assetSidebar.size')}
          value={formatFileSize(file.fileSize)}
        />
        <DetailRow
          label={t('projectDetail.assetSidebar.uploaded')}
          value={new Date(file.createdAt).toLocaleDateString()}
        />
        <DetailRow
          label={t('projectDetail.assetSidebar.status')}
          value={FILE_STATUS_CONFIG[file.status].label}
        />
      </div>
    ),
  };

  // The assignment control lives in this view, so the view has to exist even
  // with no chain assigned — otherwise there would be nowhere to assign one
  // from. Only the flag gates it.
  const approvalView: DetailSidebarView = {
    // eslint-disable-next-line i18next/no-literal-string -- sidebar view value
    value: 'approval',
    label: t('projectDetail.assetSidebar.approval'),
    content: (
      <div className="flex min-h-0 flex-1 flex-col gap-sm p-md">
        <div className="flex items-center justify-between gap-xs">
          <AssetApprovalChainControl
            projectId={projectId}
            fileId={assetId}
            folderId={folderId}
            canManage={props.canManageApprovalChain}
          />
        </div>

        {approvalChain && (
          <ApprovalTimeline projectId={projectId} assetId={assetId} folderId={folderId} />
        )}

        {!approvalChain && !isLoadingApprovalChain && !props.canManageApprovalChain && (
          <EmptyState
            title={t('projectDetail.assetSidebar.noApprovalChainAssigned')}
            description={t('projectDetail.assetSidebar.onlyChainManagersCanAssign')}
          />
        )}
      </div>
    ),
  };

  return (
    <DetailSidebar
      dataTestId="asset-sidebar"
      resizeHandleTestId="asset-sidebar-resize-handle"
      resizeLabel={t('projectDetail.assetSidebar.resizePanel')}
      view={view}
      width={props.width}
      onResize={props.onResize}
      onResizeEnd={props.onResizeEnd}
      views={
        props.showApprovalPanel
          ? [commentsView, detailsView, approvalView]
          : [commentsView, detailsView]
      }
    />
  );
}

function DetailRow(props: { label: string; value: string; truncate?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-md">
      <span className="shrink-0 text-sm text-muted-foreground">{props.label}</span>
      {props.truncate ? (
        <div className="min-w-0 flex-1">
          <TruncatedText value={props.value} className="text-right text-sm font-medium" />
        </div>
      ) : (
        <span className="text-right text-sm font-medium">{props.value}</span>
      )}
    </div>
  );
}
